// src/pages/DriverHome.jsx
import React, { useEffect, useState, useRef } from "react";
import { ref, onValue, get, set, update } from "firebase/database";
import { db, auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getStorage, ref as sRef, uploadBytes, getDownloadURL } from "firebase/storage";
import "leaflet-routing-machine";
import "./driver.css";
import "./driverLayout.css";
import { FaIdCard } from "react-icons/fa";
import { FaMap, FaSun, FaMoon } from "react-icons/fa";
import { runTransaction } from "firebase/database";
import useAuthUser from "../pages/useAuthUser";
import { FaWhatsapp } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { changeLanguage } from "../i18n";
import { formatCurrency } from "../utils/formatCurrency";
import Market from "../components/Market";
import DriverProfileOverlay from "../components/DriverProfileOverlay";
import PassengerProfileOverlay from "../components/PassengerProfileOverlay";
import PassengerSocialProfile from "../components/PassengerSocialProfile";
// ----------------- Leaflet Marker Fix -----------------
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const dropoffIcon = L.divIcon({
  className: "dropoff-dot",
  html: `<div style="width:14px;height:14px;background:red;border-radius:50%;border:2px solid white;"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const RecenterMap = ({ position }) => {
  const map = useMap();

  useEffect(() => {
    if (!isValidCoords(position)) return;

    map.setView(
      [Number(position[0]), Number(position[1])],
      map.getZoom(),
      { animate: false }
    );
  }, [position, map]);

  return null;
};

const isValidCoords = (coords) => {
  if (!coords) return false;
  const lat = Number(coords[0]);
  const lng = Number(coords[1]);
  return (lat !== undefined && lng !== undefined && !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0);
};

const RideRouting = ({ pickup, dropoff }) => {
  const map = useMap();
  const routingRef = useRef(null);

  useEffect(() => {
    if (!map || !pickup || !dropoff) return;
    if (routingRef.current) return;

    const routing = L.Routing.control({
      waypoints: [L.latLng(pickup[0], pickup[1]), L.latLng(dropoff[0], dropoff[1])],
      router: L.Routing.osrmv1({ serviceUrl: "https://router.project-osrm.org/route/v1" }),
lineOptions: {
  styles: [
    {
      color: "#1e90ff",
      weight: 6,
      className: "driver-blue-route-glow"
    }
  ]
},

      addWaypoints: false, draggableWaypoints: false, createMarker: () => null,
      fitSelectedRoutes: true, show: false, collapsible: false,
    }).addTo(map);

    const container = routing.getContainer();
    if (container) container.style.display = "none";
    routingRef.current = routing;

    return () => {
      if (routingRef.current && map) {
        try {
          routingRef.current.getPlan().setWaypoints([]);
          map.removeControl(routingRef.current);
        } catch (e) {}
        routingRef.current = null;
      }
    };
  }, [map, pickup, dropoff]);

  return null;
};

function getDistance([lat1, lng1], [lat2, lng2]) {
  const R = 6371;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lng2 - lng1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return (R * c).toFixed(2);
}

const useRideStream = () => {
  const [rides, setRides] = useState([]);
  useEffect(() => {
    let mounted = true;
    const ridesRef = ref(db, "rides");
    let unsubscribeRides = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        if (mounted) setRides([]);
        return;
      }
      if (unsubscribeRides) unsubscribeRides();

      unsubscribeRides = onValue(ridesRef, async (snap) => {
        if (!mounted) return;
        const data = snap.val() || {};
        const passengerCache = {};
      const validRides = [];

let driverProfile = {};
try {
  const driverSnap = await get(ref(db, `drivers/${currentUser.uid}`));
  driverProfile = driverSnap.val() || {};
} catch (err) {
  driverProfile = {};
}

for (const [id, ride] of Object.entries(data)) {

 // 🔒 ADD THIS HERE (FINAL SAFETY)
if (!ride?.status || ride.status === "expired" || !ride.passengerId) {
  continue;
}

// always show my accepted ride so Complete Ride button appears
if (ride.status === "accepted" && ride.driverId === currentUser.uid) {
  validRides.push({ id, ...ride });
  continue;
}

// hide completed from map, but history/finance will show them
// hide completed from map, but history/finance will show them
if (
  ride.status === "completed" ||
  ride.status === "cancelled_by_passenger" ||
  ride.tripEnded === true
) {
  continue;
}

const hasService =
  Array.isArray(driverProfile.serviceTypes)
    ? driverProfile.serviceTypes.length > 0
    : !!driverProfile.serviceType;

if (!hasService) {
  continue;
}

  if (ride.status !== "pending") continue;

  const isRental = ride.serviceType === "rental";

 const driverServices =
  Array.isArray(driverProfile.serviceTypes)
    ? driverProfile.serviceTypes
    : driverProfile.serviceType
      ? [driverProfile.serviceType]
      : [];

const sameService =
  driverServices.includes(
    String(ride.serviceType)
      .trim()
      .toLowerCase()
  );

  const sameCity =
    driverProfile.city &&
    ride.city &&
    String(driverProfile.city).trim().toLowerCase() ===
      String(ride.city).trim().toLowerCase();

const sameProvince =
  driverProfile.province &&
  ride.province &&
  String(driverProfile.province).trim().toLowerCase() ===
    String(ride.province).trim().toLowerCase();

  const driverPos = Array.isArray(driverProfile.position)
    ? [Number(driverProfile.position[0]), Number(driverProfile.position[1])]
    : null;

  const ridePickup = ride.pickup
    ? [
        Number(ride.pickup[0] ?? ride.pickup.lat ?? ride.pickup.latitude),
        Number(ride.pickup[1] ?? ride.pickup.lng ?? ride.pickup.longitude),
      ]
    : null;

  let withinDistance = false;

  if (
    driverPos &&
    ridePickup &&
    !isNaN(driverPos[0]) &&
    !isNaN(driverPos[1]) &&
    !isNaN(ridePickup[0]) &&
    !isNaN(ridePickup[1])
  ) {
    const km = parseFloat(getDistance(driverPos, ridePickup));
    withinDistance = km <= 15;
  }

  // ✅ DIRECT REQUEST: only the targeted driver can see it
  if (ride.exclusiveDriver) {
    if (
      ride.exclusiveDriver === currentUser.uid &&
      sameService
    ) {
      validRides.push({ id, ...ride });
    }
    continue;
  }

  // ✅ SERVICE REQUEST: only drivers of that service can see it
if (isRental) {
  // rental visible only within same city or same province
  if (sameService && (sameCity || sameProvince)) {
    validRides.push({ id, ...ride });
  }
} else {
  // normal rides stay local and near
  if (sameService && (sameCity || sameProvince || withinDistance)) {
  validRides.push({ id, ...ride });
}
}
}

        const ridesWithProfiles = await Promise.all(
          validRides.map(async (ride) => {
            let passengerProfile = passengerCache[ride.passengerId];
            if (!passengerProfile) {
              try {
                const profileSnap = await get(ref(db, `passengers/${ride.passengerId}`));
                passengerProfile = profileSnap.val() || {};
                passengerCache[ride.passengerId] = passengerProfile;
              } catch (err) { passengerProfile = {}; }
            }
            return { ...ride, passengerProfile };
          })
        );
        if (mounted) setRides(ridesWithProfiles);
      });
    });

    return () => {
      mounted = false;
      unsubscribeAuth();
      if (unsubscribeRides) unsubscribeRides();
    };
  }, []); 

  return rides;
};

const DriverHome = () => {
  // 👇 The "t" function translates text. "i18n" controls the language selection.
  const { t, i18n } = useTranslation();
  const user = useAuthUser();
  const rideRequests = useRideStream(); 
  
  const [driverProfile, setDriverProfile] = useState({});
  const [selectedPickup, setSelectedPickup] = useState(null);
  const [selectedDropoff, setSelectedDropoff] = useState(null);
  const [selectedPassengerForProfile, setSelectedPassengerForProfile] = useState(null);
  const passengerIconsRef = useRef({});
  const [showMarket, setShowMarket] = useState(false);
  const [showSocialProfile, setShowSocialProfile] = useState(false);
  const [selectedLicensePlan, setSelectedLicensePlan] = useState(null);
  const userCountry = driverProfile?.country || localStorage.getItem("country") || "Papua New Guinea";
const [exchangeRates, setExchangeRates] = useState({});
const [liteMode, setLiteMode] = useState(false);

const [marketDeliveries, setMarketDeliveries] = useState([]);

useEffect(() => {
  const deliveriesRef = ref(db, "marketDeliveries");

  const unsubscribe = onValue(deliveriesRef, (snap) => {
    const data = snap.val() || {};

    const list = Object.entries(data)
      .map(([id, delivery]) => ({
        id,
        ...delivery,
      }))
      .filter((delivery) => delivery.deliveryStatus === "waiting_driver");

    setMarketDeliveries(list);
  });

  return () => unsubscribe();
}, []);



const handleAcceptMarketDelivery = async (delivery) => {
  const currentUser = auth.currentUser;
  if (!currentUser) return;

  await update(ref(db, `marketDeliveries/${delivery.deliveryId}`), {
    driverId: currentUser.uid,
    driverName: driverProfile?.name || "KinaTrip Driver",
    deliveryStatus: "driver_accepted",
    acceptedAt: Date.now(),
  });

  await update(ref(db, `marketOrders/${delivery.orderId}`), {
    driverId: currentUser.uid,
    driverAssigned: true,
    deliveryStatus: "driver_accepted",
    orderStatus: "delivery_assigned",
    updatedAt: Date.now(),
  });

  showAlert("Market delivery accepted!", "success");
};



const licensePlans = [
  {
    id: "trial",
name: t("license_trial"),
    price: 0,
    days: 30,
    dailyLimit: 3,
    premium: false,
  },

  
  {
  id: "starter7",
name: t("license_standard"),
  price: 50,
  days: 7,
  dailyLimit: 20,
  premium: false,
},

{
  id: "business14",
name: t("license_silver"),
  price: 100,
  days: 14,
  dailyLimit: 40,
  premium: true,
},

  {
    id: "premium30",
name: t("license_gold"),
    price: 300,
    days: 30,
    dailyLimit: "Unlimited",
    premium: true,
  },
];

const currencyMap = {
/* ================= AFRICA ================= */

Nigeria: "NGN",
Ghana: "GHS",
Kenya: "KES",
Uganda: "UGX",
"Tanzania": "TZS",
"South Africa": "ZAR",
Rwanda: "RWF",
Burundi: "BIF",
Ethiopia: "ETB",
Somalia: "SOS",
Djibouti: "DJF",
Eritrea: "ERN",
Sudan: "SDG",
"South Sudan": "SSP",
Egypt: "EGP",
Libya: "LYD",
Tunisia: "TND",
Algeria: "DZD",
Morocco: "MAD",
Mauritania: "MRU",

Senegal: "XOF",
Mali: "XOF",
Niger: "XOF",
"Burkina Faso": "XOF",
Benin: "XOF",
Togo: "XOF",
Guinea: "GNF",
"Guinea-Bissau": "XOF",
"Sierra Leone": "SLE",
Liberia: "LRD",
"Gambia": "GMD",
"Cape Verde": "CVE",
"Côte d’Ivoire": "XOF",

Cameroon: "XAF",
Chad: "XAF",
"Gabon": "XAF",
"Congo": "XAF",
"Democratic Republic of the Congo": "CDF",
"Central African Republic": "XAF",
"Equatorial Guinea": "XAF",

Angola: "AOA",
Namibia: "NAD",
Botswana: "BWP",
Zimbabwe: "USD",
Zambia: "ZMW",
Mozambique: "MZN",
Malawi: "MWK",
Madagascar: "MGA",
Mauritius: "MUR",
Seychelles: "SCR",
Comoros: "KMF",

Lesotho: "LSL",
Eswatini: "SZL",

"Sao Tome and Principe": "STN",
/* ================= PACIFIC ISLANDS ================= */

"Papua New Guinea": "PGK",
Australia: "AUD",
"New Zealand": "NZD",

Fiji: "FJD",
Samoa: "WST",
Tonga: "TOP",
Vanuatu: "VUV",
"Solomon Islands": "SBD",

Kiribati: "AUD",
Tuvalu: "AUD",
Nauru: "AUD",

Palau: "USD",
"Federated States of Micronesia": "USD",
"Marshall Islands": "USD",

"Cook Islands": "NZD",
Niue: "NZD",
Tokelau: "NZD",

"French Polynesia": "XPF",
"New Caledonia": "XPF",
"Wallis and Futuna": "XPF",

Guam: "USD",
"American Samoa": "USD",
"Northern Mariana Islands": "USD",

/* ================= EUROPE ================= */

France: "EUR",
Germany: "EUR",
Italy: "EUR",
Spain: "EUR",
Portugal: "EUR",
Netherlands: "EUR",
Belgium: "EUR",
Austria: "EUR",
Ireland: "EUR",
Finland: "EUR",
Greece: "EUR",
Luxembourg: "EUR",
Malta: "EUR",
Cyprus: "EUR",
Estonia: "EUR",
Latvia: "EUR",
Lithuania: "EUR",
Slovakia: "EUR",
Slovenia: "EUR",
Croatia: "EUR",

"United Kingdom": "GBP",
Scotland: "GBP",
Wales: "GBP",
"Northern Ireland": "GBP",

Switzerland: "CHF",
Norway: "NOK",
Sweden: "SEK",
Denmark: "DKK",
Iceland: "ISK",

Poland: "PLN",
"Czech Republic": "CZK",
Hungary: "HUF",
Romania: "RON",
Bulgaria: "BGN",

Ukraine: "UAH",
Russia: "RUB",
Belarus: "BYN",
Moldova: "MDL",

Turkey: "TRY",

Albania: "ALL",
Serbia: "RSD",
Bosnia: "BAM",
Montenegro: "EUR",
Kosovo: "EUR",
"North Macedonia": "MKD",

Georgia: "GEL",
Armenia: "AMD",
Azerbaijan: "AZN",

Andorra: "EUR",
Monaco: "EUR",
Liechtenstein: "CHF",
"San Marino": "EUR",
"Vatican City": "EUR",


/* ================= ASIA ================= */

India: "INR",
Indonesia: "IDR",
Malaysia: "MYR",
Singapore: "SGD",
Thailand: "THB",
Vietnam: "VND",
Philippines: "PHP",
Cambodia: "KHR",
Laos: "LAK",
Myanmar: "MMK",
Brunei: "BND",

China: "CNY",
Japan: "JPY",
SouthKorea: "KRW",
"South Korea": "KRW",
NorthKorea: "KPW",
"North Korea": "KPW",
Taiwan: "TWD",
HongKong: "HKD",
"Hong Kong": "HKD",
Macao: "MOP",

Nepal: "NPR",
Bangladesh: "BDT",
SriLanka: "LKR",
"Sri Lanka": "LKR",
Bhutan: "BTN",
Maldives: "MVR",

Kazakhstan: "KZT",
Uzbekistan: "UZS",
Turkmenistan: "TMT",
Kyrgyzstan: "KGS",
Tajikistan: "TJS",

Mongolia: "MNT",

TimorLeste: "USD",
"Timor-Leste": "USD",

Pakistan: "PKR",
Afghanistan: "AFN",

Armenia: "AMD",
Azerbaijan: "AZN",
Georgia: "GEL",




/* ================= MIDDLE EAST ================= */

"Saudi Arabia": "SAR",
"United Arab Emirates": "AED",
Qatar: "QAR",
Kuwait: "KWD",
Bahrain: "BHD",
Oman: "OMR",

Israel: "ILS",
Jordan: "JOD",
Lebanon: "LBP",
Syria: "SYP",
Iraq: "IQD",
Iran: "IRR",
Yemen: "YER",

Turkey: "TRY",

Palestine: "ILS",

Afghanistan: "AFN",
Pakistan: "PKR",

/* ================= AMERICAS ================= */

"United States": "USD",
Canada: "CAD",
Mexico: "MXN",

Brazil: "BRL",
Argentina: "ARS",
Chile: "CLP",
Colombia: "COP",
Peru: "PEN",
Venezuela: "VES",
Ecuador: "USD",
Bolivia: "BOB",
Paraguay: "PYG",
Uruguay: "UYU",
Guyana: "GYD",
Suriname: "SRD",

Guatemala: "GTQ",
Belize: "BZD",
Honduras: "HNL",
"El Salvador": "USD",
Nicaragua: "NIO",
"Costa Rica": "CRC",
Panama: "USD",

Cuba: "CUP",
Jamaica: "JMD",
Haiti: "HTG",
"Dominican Republic": "DOP",
Bahamas: "BSD",
Barbados: "BBD",
"Trinidad and Tobago": "TTD",
Grenada: "XCD",
Dominica: "XCD",
"Saint Lucia": "XCD",
"Saint Vincent and the Grenadines": "XCD",
"Antigua and Barbuda": "XCD",
"Saint Kitts and Nevis": "XCD",

Greenland: "DKK",
Bermuda: "BMD",

Aruba: "AWG",
Curacao: "ANG",
};

const getCurrencyCode = (country) => {
  if (!country) return "USD";

  const cleanCountry =
    String(country).trim();

  return currencyMap[cleanCountry] || "USD";
};

const formatLicensePrice = (
  usdPrice,
  exchangeRate,
  country
) => {
  const currency = getCurrencyCode(country);

 const localAmount =
  Number(usdPrice) *
  Number(exchangeRate || 1);

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(localAmount);
};

 const completeRide = async (ride) => {
  const currentUser = auth.currentUser;

  if (!currentUser) return;

  try {
    await update(ref(db, `rides/${ride.id}`), {
      status: "completed",
      tripEnded: true,
      completedAt: Date.now(),
    });

    await set(
      ref(db, `financeHistory/${currentUser.uid}/${ride.id}`),
      {
        rideId: ride.id,
        driverId: currentUser.uid,

        passengerId: ride.passengerId || "",
        passengerName: ride.passengerName || "",
        passengerPhone: ride.passengerPhone || "",

       pickupAddress:
  ride.pickupAddress ||
  ride.pickupLocation ||
  ride.pickup?.address ||
  "",

dropoffAddress:
  ride.dropoffAddress ||
  ride.destination ||
  ride.destinationCoords?.address ||
  ride.dropoffLocation ||
  "",

        fare: Number(ride.fare || 0),

        status: "completed",

        paymentStatus:
          ride.paymentStatus || "paid",

        timestamp:
          ride.timestamp || Date.now(),

        completedAt: Date.now(),
      }
    );

    showAlert(
      "Ride completed successfully!",
      "success"
    );

  } catch (err) {
    console.log(err);

    showAlert(
      "Failed to complete ride",
      "error"
    );
  }
};

  const handleAccept = async (ride) => {
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.uid) {
      showAlert("Session loading... Please try again in a second.", "error"); return;
    }
    const rideRef = ref(db, `rides/${ride.id}`);
    try {


const today = new Date().toLocaleDateString("en-CA");

const countRef = ref(
  db,
  `dailyAccepted/${currentUser.uid}/${today}`
);

const countSnap = await get(countRef);

const currentCount = countSnap.exists()
  ? Number(countSnap.val())
  : 0;

if (
  licenseData?.licenseType === "trial" &&
  currentCount >= 3
) {
  showAlert(
    "Trial daily limit reached. Please upgrade your license.",
    "error"
  );

  setLicensePopup(true);
  return;
}

      const result = await runTransaction(rideRef, (currentData) => {
        if (currentData === null) return currentData;
        if (currentData.status !== "pending") return;
return {
  ...currentData,

  status: "accepted",

  driverId: currentUser.uid,

  driverName: driverProfile.name || "",

  driverPhoto:
    driverProfile.photoURL ||
    driverProfile.photoUrl ||
    "",

  serviceTypes:
  driverProfile.serviceTypes || [],

primaryService:
  driverProfile.serviceTypes?.[0] || "",

  acceptedAt: Date.now(),

  sessionActive: true
};

      });
      if (!result.committed) {
        showAlert("Ride already accepted by another driver.", "error"); return;
      }
await update(ref(db, `rides/${ride.id}`), {
  sessionActive: true,

  driverId: currentUser.uid,

  driverName: driverProfile.name || "",

  driverPhoto:
    driverProfile.photoURL ||
    driverProfile.photoUrl ||
    "",

 serviceTypes:
  driverProfile.serviceTypes || [],

primaryService:
  driverProfile.serviceTypes?.[0] || "",

  acceptedAt: Date.now(),
});

      setAcceptedRideId(ride.id);

      if (licenseData?.licenseType === "trial") {
  await set(countRef, currentCount + 1);
  setDailyAccepted(currentCount + 1);
}

showAlert(t("ride_accepted_success"), "success");      
      if (selectedRide) { setProductKeyPopup(false); setSelectedRide(null); }
    } catch (error) {
      console.error("Transaction Error:", error);
      showAlert("Connection error. Please try again.", "error");
    }
  };

  const [driverPosition, setDriverPosition] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mapView, setMapView] = useState("street");
  const [licensePopup, setLicensePopup] = useState(false);
  const [licenseData, setLicenseData] = useState(null);
  const [dailyAccepted, setDailyAccepted] = useState(0);
  const [passengerPosition, setPassengerPosition] = useState(null);
  const [acceptedRideId, setAcceptedRideId] = useState(null);
  const [cashDepositMode, setCashDepositMode] = useState(false);
  const [cardMode, setCardMode] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [isGpsActive, setIsGpsActive] = useState(false);
  const [liveChatPopup, setLiveChatPopup] = useState(false); 
  const [liveChatMessages, setLiveChatMessages] = useState([]); 
  const [newLiveMsg, setNewLiveMsg] = useState("");
  const [fileUpload, setFileUpload] = useState(null);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [enteredKey, setEnteredKey] = useState("");
  const [selectedRide, setSelectedRide] = useState(null);
  const [productKeyPopup, setProductKeyPopup] = useState(false);
  const [historyPopup, setHistoryPopup] = useState(false);
  const [rideHistory, setRideHistory] = useState([]);
  const [alertMessage, setAlertMessage] = useState("");

const [driverAlert, setDriverAlert] = useState({
  show: false,
  message: "",
  type: "success",
});

const showDriverAlert = (message, type = "success") => {
  setDriverAlert({
    show: true,
    message,
    type,
  });

  setTimeout(() => {
    setDriverAlert({
      show: false,
      message: "",
      type: "success",
    });
  }, 4000);
};


  const [alertType, setAlertType] = useState(""); 
  const [focusedRideId, setFocusedRideId] = useState(null);
  const gpsWatchRef = useRef(null);
  const wakeLockRef = useRef(null); 

const [paymentFullName, setPaymentFullName] = useState("");
const [paymentPhone, setPaymentPhone] = useState("");
const [paymentAmount, setPaymentAmount] = useState("");
const [paymentReference, setPaymentReference] = useState("");

  const showAlert = (message, type) => {
    setAlertMessage(message); setAlertType(type);
    setTimeout(() => setAlertMessage(""), 3000);
  };

useEffect(() => {

  const unsubscribeAuth = onAuthStateChanged(
    auth,
    (currentUser) => {

      if (!currentUser) return;

      const ridesRef = ref(db, "rides");

      const unsubscribeRides = onValue(
        ridesRef,
        (snapshot) => {

          const rides = snapshot.val() || {};

          Object.entries(rides).forEach(
            ([rideId, ride]) => {

              if (!ride) return;

              const belongsToDriver =
                ride.driverId === currentUser.uid;

              if (!belongsToDriver) return;

              // PASSENGER ACCEPTED
              if (
                ride.passengerResponse === "accepted" &&
                !ride.driverNotifiedAccepted
              ) {

                console.log(
                  "PASSENGER ACCEPTED:",
                  ride
                );

                showDriverAlert(
                  `🎉 ${
                    ride.passengerName || "Passenger"
                  } accepted your ride!`,
                  "success"
                );

                update(
                  ref(db, `rides/${rideId}`),
                  {
                    driverNotifiedAccepted: true,
                  }
                );
              }

              // PASSENGER CANCELLED
              if (
                ride.passengerResponse === "rejected" &&
                !ride.driverNotifiedRejected
              ) {

                showDriverAlert(
                  `❌ ${
                    ride.passengerName || "Passenger"
                  } cancelled the ride`,
                  "error"
                );

                update(
                  ref(db, `rides/${rideId}`),
                  {
                    status:
                      "cancelled_by_passenger",

                    driverNotifiedRejected: true,
                  }
                );
              }
            }
          );
        }
      );

      return () => unsubscribeRides();
    }
  );

  return () => unsubscribeAuth();

}, []);





  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) return;
      const driverRef = ref(db, `drivers/${currentUser.uid}`);
      const licRef = ref(db, `licenses/${currentUser.uid}`);
      const today = new Date().toLocaleDateString("en-CA");

      onValue(driverRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setDriverProfile(data);
          if (data.position && data.position.length === 2) {
            setDriverPosition(data.position);
          }
        }
      });

      try {
        const [licSnap, countSnap] = await Promise.all([ get(licRef), get(ref(db, `dailyAccepted/${currentUser.uid}/${today}`)) ]);
if (
  !licSnap.exists() ||
  (
    licSnap.val()?.licenseType === "trial" &&
    Date.now() > licSnap.val()?.expiry
  )
) {

          const now = Date.now();
          const trialLicense = { status: "approved", licenseType: "trial", activatedAt: now, expiry: now + 30 * 24 * 60 * 60 * 1000, dailyLimit: 3, method: "auto-trial" };
          await set(licRef, trialLicense);
          setLicenseData(trialLicense);
          showAlert("🎉 30-Day Free Trial Activated! (3 rides daily)", "success");
        } else {
          setLicenseData(licSnap.val());
        }
        if (countSnap.exists()) setDailyAccepted(countSnap.val());
      } catch (err) { console.log("Session init error", err); }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    const liveRef = ref(db, `chats/admin/${user.uid}`);
    const unsubscribeChat = onValue(liveRef, (snap) => {
      const data = snap.val() || {};
      const msgs = Object.entries(data).map(([id, msg]) => ({ id, ...msg }));
      setLiveChatMessages(msgs);
      setUnreadChatCount(msgs.filter(m => !m.read).length);
    });
    return () => { unsubscribeChat(); };
  }, [user]);

  const submitProductKey = async (type = "trial") => {
    if (!user || !enteredKey.trim()) return;
    const keyRef = ref(db, `productKeys/${enteredKey}`);
    const snap = await get(keyRef);

    if (!snap.exists()) { showAlert("Invalid Product Key. Access Denied.", "error"); return; }
    const keyData = snap.val();
    if (keyData.issuedTo) { showAlert("This Product Key is already used.", "error"); return; }

    const now = Date.now();
    let expiry = type === "trial" ? now + 30 * 24 * 60 * 60 * 1000 : now + 10 * 24 * 60 * 60 * 1000;
    let dailyLimit = type === "trial" ? 3 : 10;
    let licenseType = type === "trial" ? "trial" : "paid";

    const updatedLicense = { productKey: enteredKey, status: "approved", activatedAt: now, expiry, dailyLimit, licenseType, method: "productKey" };
    await set(ref(db, `licenses/${user.uid}`), updatedLicense);
    await update(ref(db, `productKeys/${enteredKey}`), { issuedTo: user.uid, issuedAt: now });
    setLicenseData(updatedLicense);
    showAlert(licenseType === "trial" ? "Trial License Activated!" : "Paid License Activated!", "success");
    setProductKeyPopup(false); setEnteredKey("");

    if (selectedRide) { await handleAccept(selectedRide); setSelectedRide(null); }
  };

  useEffect(() => {
    if (!user) return;
    const ridesRef = ref(db, "rides");
    return onValue(ridesRef, async (snap) => {
      const data = snap.val() || {};
      const historyArray = [];
      const passengersSnap = await get(ref(db, "passengers"));
      const passengersData = passengersSnap.val() || {};
      for (const [id, ride] of Object.entries(data)) {


if (!ride?.status || !ride.passengerId) {
  continue;
}

if (
  ride.driverId === user.uid &&
  (
    ride.status === "accepted" ||
    ride.status === "completed" ||
    ride.tripEnded === true
  )
) {
  historyArray.push({
    id,
    ...ride,
    passengerProfile: passengersData[ride.passengerId] || {},
  });
}

      }
      setRideHistory(historyArray);
    });
  }, [user]);

  const isLicenseValid = () => {
    if (!licenseData) return false;
    return licenseData.status === "approved" && licenseData.expiry && Date.now() < licenseData.expiry;
  };

  const [licenseCountdown, setLicenseCountdown] = useState("");

  useEffect(() => {
    if (!licenseData?.expiry) return;
    const interval = setInterval(() => {
      const diff = licenseData.expiry - Date.now();
      if (diff <= 0) {
        setLicenseCountdown("Expired");
        if (user) update(ref(db, `licenses/${user.uid}`), { status: "expired" });
        clearInterval(interval); return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      setLicenseCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    }, 1000); 
    return () => clearInterval(interval);
  }, [licenseData]);

  useEffect(() => {
    if (!user) return;
    const resetDaily = async () => {
      const today = new Date().toLocaleDateString("en-CA");   
      const countRef = ref(db, `dailyAccepted/${user.uid}/${today}`);
      const snap = await get(countRef);
      if (!snap.exists()) { await set(countRef, 0); setDailyAccepted(0); }
    };
    resetDaily();
    const msUntilMidnight = new Date().setHours(24, 0, 0, 0) - new Date();
    const midnightTimeout = setTimeout(() => {
      resetDaily(); setInterval(resetDaily, 24 * 60 * 60 * 1000);
    }, msUntilMidnight);
    return () => clearTimeout(midnightTimeout);
  }, [user]);

useEffect(() => {

  const currentUser = auth.currentUser;

  if (!currentUser) return;

  if (!navigator.geolocation) return;

  const watchId =
    navigator.geolocation.watchPosition(

      async (position) => {

        const lat =
          position.coords.latitude;

        const lng =
          position.coords.longitude;

        const liveCoords = [
          lat,
          lng
        ];

        // Update local state
        setDriverPosition(
          liveCoords
        );

        try {

          // Detect country
          const response =
            await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
            );

          const data =
            await response.json();

          if (
            data.address &&
            data.address.country
          ) {
            localStorage.setItem(
              "country",
              data.address.country
            );
          }

        } catch (err) {

          console.log(
            "Could not auto-detect country"
          );
        }

        // Realtime Firebase update
        await update(
          ref(
            db,
            `drivers/${currentUser.uid}`
          ),
          {
            livePosition:
              liveCoords,

            position:
              liveCoords,

            lastGpsUpdate:
              Date.now(),

            online: true,
          }
        );
      },

      (error) => {
        console.log(
          "Driver GPS Error:",
          error.message
        );
      },

      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 5000,
      }
    );

  return () => {
    navigator.geolocation.clearWatch(
      watchId
    );
  };

}, []);



  useEffect(() => {
    if (!acceptedRideId) return;
    const passengerRef = ref(db, `rides/${acceptedRideId}/passengerPosition`);
    const unsubscribe = onValue(passengerRef, (snap) => {
      if (snap.val()) setPassengerPosition(snap.val());
    });
    return () => unsubscribe();
  }, [acceptedRideId]);

  const requestWakeLock = async () => {
    try { if ("wakeLock" in navigator) wakeLockRef.current = await navigator.wakeLock.request("screen"); } 
    catch (err) {}
  };

  const releaseWakeLock = async () => {
    if (wakeLockRef.current !== null) {
      try { await wakeLockRef.current.release(); wakeLockRef.current = null; } 
      catch (err) {}
    }
  };


useEffect(() => {
  fetch("https://open.er-api.com/v6/latest/USD")
    .then((res) => res.json())
    .then((data) => {
      setExchangeRates(data.rates || {});
    })
    .catch((err) => {
      console.log("Exchange API failed", err);
    });
}, []);


  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible" && isGpsActive) await requestWakeLock();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isGpsActive]);

  const startGPS = async () => { 
    if (!user || !navigator.geolocation) { showAlert(t('gps_denied') || "GPS not supported.", "error"); return; }
    if (gpsWatchRef.current !== null) return;

    setIsGpsActive(true); await requestWakeLock(); 
    let lastUpdate = 0;

    gpsWatchRef.current = navigator.geolocation.watchPosition(
      async (position) => {
        const now = Date.now();
if (now - lastUpdate < 10000) return;        
        lastUpdate = now;
        const coords = [position.coords.latitude, position.coords.longitude];
        setDriverPosition(coords);
        try { await update(ref(db, `drivers/${user.uid}`), { position: coords, online: true, lastUpdated: now }); } catch (e) {}
      }, 
      (error) => {
        if (error.code === 1) { showAlert(t('gps_denied') || "Permission denied. Check phone settings.", "error"); setIsGpsActive(false); releaseWakeLock(); }
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 30000 }
    );
  };



useEffect(() => {
  const user = auth.currentUser;

  if (!user) return;

  if (!navigator.geolocation) return;

  const watchId = navigator.geolocation.watchPosition(
    async (position) => {

      const liveCoords = [
        position.coords.latitude,
        position.coords.longitude,
      ];

      // update local state

      // save realtime GPS to Firebase
      await update(
        ref(db, `passengers/${user.uid}`),
        {
          livePosition: liveCoords,
          lastGpsUpdate: Date.now(),
        }
      );
    },

    (err) => {
      console.log("Passenger GPS Error:", err);
    },

    {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 5000,
    }
  );


  return () => {
    navigator.geolocation.clearWatch(watchId);
  };
}, []);




  const stopGPS = () => {
    if (!user) return;
    if (gpsWatchRef.current !== null) { navigator.geolocation.clearWatch(gpsWatchRef.current); gpsWatchRef.current = null; }
    update(ref(db, `drivers/${user.uid}`), { online: false });
    setIsGpsActive(false); releaseWakeLock(); 
  };

  const sendLiveMessage = async () => {
    if (!user || (!newLiveMsg.trim() && !fileUpload)) return;
    const id = Date.now();
    const msgData = { content: newLiveMsg || "", sender: "driver", timestamp: id, read: false, type: fileUpload ? "file" : "text", fileUrl: null };
    if (fileUpload) {
      const storage = getStorage(); const fileRef = sRef(storage, `liveChatFiles/${user.uid}/${fileUpload.name}`);
      await uploadBytes(fileRef, fileUpload); msgData.fileUrl = await getDownloadURL(fileRef);
    }
    await set(ref(db, `chats/admin/${user.uid}/${id}`), msgData);
    setNewLiveMsg(""); setFileUpload(null);
  };

  const handleUploadScreenshot = async (e) => {
    const file = e.target.files[0];
    if (!file || !user) return;
    const storage = getStorage(); const fileRef = sRef(storage, `licenses/${user.uid}/screenshot.jpg`);
    await uploadBytes(fileRef, file); setScreenshotUrl(await getDownloadURL(fileRef));
  };

const submitCashPayment = async () => {
  if (!user || !screenshotUrl) {
    showAlert("Please upload payment receipt screenshot.", "error");
    return;
  }

  if (
    !paymentFullName ||
    !paymentPhone ||
    !paymentAmount ||
    !paymentReference
  ) {
    showAlert("Please complete all payment details.", "error");
    return;
  }

  const now = Date.now();
const expiry =
  now +
  selectedLicensePlan.days *
    24 *
    60 *
    60 *
    1000;  

 await set(ref(db, `licenses/${user.uid}`), {
 method: "manual_partner_payment",
usdAmount: selectedLicensePlan.price,
localAmount: Number(paymentAmount),
country: userCountry,
currency: getCurrencyCode(userCountry),
partnerStatus: "pending_verification",

  payerName: paymentFullName,
  payerPhone: paymentPhone,
  bankReference: paymentReference,
  activatedAt: now,
  expiry,
  status: "pending",
  screenshotUrl,
  productKey: null,
  licenseType: "paid",
  planId: selectedLicensePlan.id,
  planName: selectedLicensePlan.name,
  dailyLimit: selectedLicensePlan.dailyLimit,
});

  showAlert(
    "Payment submitted successfully. Waiting for admin approval.",
    "success"
  );

  setCashDepositMode(false);
  setLicensePopup(false);

  setPaymentFullName("");
  setPaymentPhone("");
setPaymentAmount("");  
  setPaymentReference("");
  setScreenshotUrl(null);
};







const handleOnlinePayment = async (selectedProvider) => {  
  if (!user) {
    showAlert("User not found. Please login again.", "error");
    return;
  }

  const country = driverProfile?.country || userCountry;
const amount = selectedLicensePlan?.price;  

  try {
    const response = await fetch("http://localhost:5000/create-license-payment", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    body: JSON.stringify({
  driverId: user.uid,
  driverName: driverProfile?.name || paymentFullName,
  phone: driverProfile?.phone || paymentPhone,
  email: driverProfile?.email || "",
  country,
  amount,
  selectedProvider,
}),
    });

    const data = await response.json();

    if (data.checkoutUrl) {
      window.location.href = data.checkoutUrl;
    } else {
      showAlert("Payment link could not be created.", "error");
    }
  } catch (error) {
    console.error(error);
    showAlert("Online payment failed to start. Check backend server.", "error");
  }
};


return (
  <>
    <div className="driver-page">

{driverAlert.show && (
  <div
    style={{
      position: "fixed",
      top: "20px",
      left: "50%",
      transform: "translateX(-50%)",
      background:
        driverAlert.type === "success"
          ? "#28a745"
          : "#dc3545",
      color: "white",
      padding: "14px 22px",
      borderRadius: "12px",
      zIndex: 999999,
      fontWeight: "bold",
      boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
      animation: "fadeIn 0.3s ease",
    }}
  >
    {driverAlert.message}
  </div>
)}


      {alertMessage && (
        <div style={{ position: "fixed", top: "20px", right: "20px", padding: "14px 20px", borderRadius: "10px", fontWeight: "600", color: "#fff", backgroundColor: alertType === "error" ? "#e74c3c" : "#2ecc71", boxShadow: "0 6px 18px rgba(0,0,0,0.25)", zIndex: 9999, animation: "slideIn 0.3s ease", minWidth: "250px" }}>
          {alertType === "error" ? "❌ " : "✅ "} {alertMessage}
        </div>
      )}

      {!sidebarOpen && (
        <button onClick={() => setSidebarOpen(true)} style={{ position: "fixed", top: "20px", left: "50px", zIndex: 1000, background: "#007bff", color: "white", border: "none", width: "45px", height: "45px", borderRadius: "50%", fontSize: "20px", cursor: "pointer", boxShadow: "0 4px 10px rgba(0,0,0,0.25)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          ☰
        </button>
      )}

      <div style={{ position: "relative", height: "100vh", width: "100%" }}>
        <button onClick={() => { if (isGpsActive) stopGPS(); else startGPS(); }} style={{ position: "absolute", top: 10, right: 10, zIndex: 1000, backgroundColor: isGpsActive ? "green" : "#0a4ecc", color: "#fff", padding: "10px", borderRadius: "50%", cursor: "pointer", border: "none" }}>
          {isGpsActive ? <FaSun size={20} /> : <FaMoon size={20} />}
        </button>

{marketDeliveries.length > 0 && (
  <div className="market-delivery-panel">
    <h4>Market Deliveries</h4>

    {marketDeliveries.map((delivery) => (
      <div key={delivery.deliveryId} className="market-delivery-card">
        <b>{delivery.itemTitle}</b>
        <p>Pickup: {delivery.pickupLocation}</p>
        <p>Dropoff: {delivery.dropoffLocation}</p>
        <p>Amount: K{delivery.amount}</p>

        <button onClick={() => handleAcceptMarketDelivery(delivery)}>
          Accept Delivery
        </button>
      </div>
    ))}
  </div>
)}


        <MapContainer center={driverPosition || [-6.73, 147.00]} zoom={13} style={{ width: "100%", height: "100%" }}>
          <TileLayer url={ mapView === "street" ? "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" : "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png" } />

          {rideRequests.map((ride) => {
            const passenger = ride.passengerProfile || {};
            const passengerName = passenger.name || ride.passengerName || "Passenger";
            const passengerPhoto = passenger.photoURL || passenger.photoUrl || passenger.avatar || "/default-avatar.png";
            const passengerPhone = passenger.phone || "No Phone";

            const isRental = ride.serviceType === "rental";

const pickupCoords = ride.pickup
  ? [
      Number(ride.pickup[0] ?? ride.pickup.lat ?? ride.pickup.latitude),
      Number(ride.pickup[1] ?? ride.pickup.lng ?? ride.pickup.longitude),
    ]
  : null;

const dropoffCoords = ride.destinationCoords
  ? [
      Number(ride.destinationCoords[0] ?? ride.destinationCoords.lat ?? ride.destinationCoords.latitude ?? 0),
      Number(ride.destinationCoords[1] ?? ride.destinationCoords.lng ?? ride.destinationCoords.longitude ?? 0),
    ]
  : null;

// for rentals, use driver position as popup anchor if no pickup coords
const markerPosition = isValidCoords(pickupCoords) ? pickupCoords : null;

if (!markerPosition) return null;

            const isAcceptedByMe = ride.status === "accepted" && ride.driverId === auth.currentUser?.uid;
            const isFocused = focusedRideId === ride.id;

            const iconKey = `${ride.id}-${ride.fare}-${isAcceptedByMe}`;
            let cachedPickupIcon = passengerIconsRef.current[iconKey];
            if (!cachedPickupIcon) {
              cachedPickupIcon = L.divIcon({
                className: "ride-circle-on-map",
                html: `
                  <div class="ride-circle" style="border-color: ${isAcceptedByMe ? '#00c853' : '#0a4ecc'}">
                    <img src="${passengerPhoto}" alt="${passengerName}" class="ride-avatar"/>
                    <span class="ride-amount" style="background: ${isAcceptedByMe ? '#00c853' : '#333'}">
                      ${formatCurrency(ride.fare, userCountry)}
                    </span>
                  </div>
                `,
                iconSize: [50, 50], iconAnchor: [25, 25],
              });
              passengerIconsRef.current[iconKey] = cachedPickupIcon;
            }

            return (
              <Marker
  key={ride.id} position={markerPosition} icon={cachedPickupIcon}
                eventHandlers={{
                  click: () => {
                    if (isFocused) { setFocusedRideId(null); setSelectedPickup(null); setSelectedDropoff(null); } 
                    else { setFocusedRideId(ride.id); setSelectedPickup(pickupCoords); setSelectedDropoff(dropoffCoords); }
                  },
                }}
              >
                  <Popup minWidth={300} maxWidth={320} autoClose={false}>
                  <div style={{ padding: "12px", color: "#fff", backgroundColor: isAcceptedByMe ? "#1b3320" : "#111", borderRadius: "12px", border: isAcceptedByMe ? "2px solid #00c853" : "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                  <img 
  src={passengerPhoto} 
  onClick={(e) => {
    e.stopPropagation();
    setSelectedPassengerForProfile({
      ...passenger,
      uid: ride.passengerId
    });
  }}
  style={{ 
    width: "50px", 
    height: "50px", 
    borderRadius: "50%", 
    border: "2px solid #fff", 
    cursor: "pointer" 
  }} 
  alt={passengerName} 
  title="Click to view Passenger Profile"
/>
                      <div>
                        <h4 style={{ margin: 0 }}>{passengerName}</h4>
                        {/* 👇 Translated: "Your Passenger" / "New Request" */}
                        <small style={{ color: "#bbb" }}>{isAcceptedByMe ? `✅ ${t('your_passenger') || "Your passenger"}` : `🚖 ${t('new_request') || "New request"}`}</small>
                      </div>
                    </div>

{ride.serviceType === "rental" ? (
  <div
    style={{
      background: "rgba(255,255,255,0.1)",
      padding: "8px",
      borderRadius: "8px",
      marginBottom: "10px",
    }}
  >
    <p style={{ margin: "2px 0", fontSize: "16px", color: "#00c853" }}>
      <b>Total: {formatCurrency(ride.totalCost || ride.fare || 0, userCountry)}</b>
    </p>
    <p style={{ margin: "2px 0", fontSize: "13px" }}>
      <b>Pickup:</b> {ride.pickupLocation || "N/A"}
    </p>
    <p style={{ margin: "2px 0", fontSize: "13px" }}>
      <b>Return:</b> {ride.returnLocation || "N/A"}
    </p>
    <p style={{ margin: "2px 0", fontSize: "13px" }}>
      <b>Vehicle:</b> {ride.vehicleType || "N/A"}
    </p>
    <p style={{ margin: "2px 0", fontSize: "13px" }}>
<b>Days:</b> {ride.rentalDays || 0}      
    </p>
    <p style={{ margin: "2px 0", fontSize: "13px" }}>
      <b>Driver Option:</b> {ride.driverOption || "N/A"}
    </p>
    <p style={{ margin: "2px 0", fontSize: "13px" }}>
      <b>Phone:</b> {ride.passengerPhone || passengerPhone}
    </p>
  </div>
) : (
  <div
    style={{
      background: "rgba(255,255,255,0.1)",
      padding: "8px",
      borderRadius: "8px",
      marginBottom: "10px",
    }}
  >
    <p style={{ margin: "2px 0", fontSize: "16px", color: "#00c853" }}>
      <b>{t("fare") || "Fare"}: {formatCurrency(ride.fare, userCountry)}</b>
    </p>

    {isAcceptedByMe ? (
      <>
        <p style={{ margin: "4px 0", fontSize: "13px" }}>
          <b>{t("phone") || "Phone"}:</b> {passengerPhone}
        </p>
        {ride.province && (
          <p style={{ margin: "2px 0", fontSize: "13px" }}>
            <b>{t("area") || "Area"}:</b> {ride.province}
          </p>
        )}
      </>
    ) : (
      <p style={{ margin: "4px 0", fontSize: "12px", color: "#bbb", fontStyle: "italic" }}>
        {t("accept_to_view") || "Accept ride to view contact & area details"}
      </p>
    )}
  </div>
)}

{isAcceptedByMe ? (
  <>
    <div style={{ display: "flex", gap: "8px" }}>
      <button
        onClick={() => window.open(`tel:${passengerPhone}`)}
        style={{
          flex: 1,
          padding: "10px",
          background: "#00c853",
          color: "#fff",
          border: "none",
          borderRadius: "6px",
          fontWeight: "bold",
          cursor: "pointer",
        }}
      >
        📞 {t("call") || "Call"}
      </button>

      <button
        onClick={() =>
          window.open(`https://wa.me/${passengerPhone.replace(/\D/g, "")}`)
        }
        style={{
          flex: 1,
          padding: "10px",
          background: "#25D366",
          color: "#fff",
          border: "none",
          borderRadius: "6px",
          fontWeight: "bold",
          cursor: "pointer",
        }}
      >
        <FaWhatsapp size={18} /> {t("chat") || "Chat"}
      </button>
    </div>

    <button
      onClick={() => completeRide(ride)}
      style={{
        width: "100%",
        marginTop: "10px",
        padding: "10px",
        background: "#ff9800",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        fontWeight: "bold",
        cursor: "pointer",
      }}
    >
      ✅ Complete Ride
    </button>
  </>
) : (
  <button
    onClick={() => {
      if (isLicenseValid()) {
        handleAccept(ride);
      } else {
        setSelectedRide(ride);
        setProductKeyPopup(true);
      }
    }}
    style={{
      width: "100%",
      padding: "10px",
      backgroundColor: isLicenseValid() ? "#0a4ecc" : "#ff9800",
      color: "#fff",
      border: "none",
      borderRadius: "6px",
      fontWeight: "bold",
      cursor: "pointer",
    }}
  >
    {isLicenseValid()
      ? t("accept_ride") || "Accept Ride"
      : `🔑 ${t("enter_key") || "Enter Product Key"}`}
  </button>
)}




                  </div>
                </Popup>
              </Marker>
            );
          })}

          {selectedPickup && selectedDropoff && (
            <>
              <Marker position={selectedDropoff} icon={dropoffIcon} />
{selectedPickup && selectedDropoff && (
  <>
    <Marker position={selectedDropoff} icon={dropoffIcon} />

    <RideRouting
      pickup={selectedPickup}
      dropoff={selectedDropoff}
    />
  </>
)}           
            </>
          )}
{isValidCoords(driverPosition) && (
  <RecenterMap position={driverPosition} />
)}         
        </MapContainer>
      </div>

<div
  className={`sidebar-menu ${sidebarOpen ? "open" : "collapsed"}`}
  style={{
    display: "flex",
    flexDirection: "column",
    height: "100vh",
  }}
>

       <button
  className="sidebar-toggle-btn"
  onClick={() => setSidebarOpen(!sidebarOpen)}
>
  {sidebarOpen ? t("back_to_map") : "☰"}
</button>

        {sidebarOpen && (
          <>
      <div className="profile-header" onClick={() => {
              setShowSocialProfile(true); setSidebarOpen(false);
              if (rideRequests.length > 0) {
                const firstRide = rideRequests[0];
                const pickupCoords = firstRide.pickup ? [Number(firstRide.pickup[0] ?? firstRide.pickup.lat ?? firstRide.pickup.latitude), Number(firstRide.pickup[1] ?? firstRide.pickup.lng ?? firstRide.pickup.longitude)] : null;
                const dropoffCoords = firstRide.destinationCoords ? [Number(firstRide.destinationCoords[0] ?? firstRide.destinationCoords.lat ?? firstRide.destinationCoords.latitude), Number(firstRide.destinationCoords[1] ?? firstRide.destinationCoords.lng ?? firstRide.destinationCoords.longitude)] : null;
                if (isValidCoords(pickupCoords) && isValidCoords(dropoffCoords)) {
                  setSelectedPickup(pickupCoords); setSelectedDropoff(dropoffCoords); setFocusedRideId(firstRide.id);
                }
              }
            }}>
              <img src={driverProfile.photoURL || "/default-avatar.png"} className="ride-avatar-sidebar" alt={driverProfile.name || t('driver')} />
              <h3>{driverProfile.name}</h3>
              <p>{driverProfile.phone}</p>
              
              {/* 👇 FIXED: Translated License, Not Paid, and Expires in */}
              <p>{t('license') || "License"}: {licenseData?.status || t('not_paid') || "Not Paid"} | {t('expires_in') || "Expires in"}: {licenseCountdown || "--"}</p>
              
              {/* 👇 FIXED: Comment moved safely OUTSIDE the parentheses! */}
              {licenseData?.productKey && (
                <p>{t('product_key') || "Product Key"}: <span style={{ color: "green", cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); alert(`${t('product_key') || "Product Key"}: ${licenseData.productKey}`); }}>{licenseData.productKey} 🔑</span></p>
              )}
            </div>

        <ul className="sidebar-menu-links">
              <li 
                onClick={() => {
                  const myActiveRide = rideRequests.find((r) => r.status === "accepted" && r.driverId === auth.currentUser?.uid);
                  const passengerPhone = myActiveRide?.passengerProfile?.phone;
                  if (myActiveRide && passengerPhone) { window.open(`https://wa.me/${passengerPhone.replace(/\D/g, "")}`, "_blank"); } 
                  else { alert("You don't have an active ride or the passenger's contact is unavailable."); }
                }}
                style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", color: "#25D366", fontWeight: "600" }}
              >
                <FaWhatsapp size={20} /> {t('chat') || "Chat with Passenger"}
              </li>

              {/* 👇 FIXED: Translated Map */}
              <li onClick={() => setMapView((p) => (p === "street" ? "satellite" : "street"))}>
                <FaMap /> {t('map') || "Map"}
              </li>

              {/* 👇 FIXED: Translated License */}
<li
  onClick={() => {
    setCashDepositMode(false);
    setCardMode(false);
    setLicensePopup(true);
  }}
>                
                <FaIdCard /> {t('license') || "License"}
              </li>

              {/* Leave KinaMarket alone (Brand names don't translate) */}
             <li onClick={() => { setShowMarket(true); setSidebarOpen(false); }}>
                🛒 {t('kinamarket') || "KinaMarket"}
              </li>

              {/* 👇 FIXED: Translated Support */}
              <li onClick={() => window.open("https://wa.me/67582164931", "_blank")} style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", color: "#25D366", fontWeight: "600" }}>
                <FaWhatsapp size={20} /> {t('support') || "Support"}
              </li>

              <li onClick={() => { stopGPS(); auth.signOut(); window.location.href = "/"; }}>
                🔒 {t('logout') || "Logout"}
              </li>

            <li style={{ marginTop: "10px", padding: "0", display: "flex", flexDirection: "column", gap: "5px", border: "none", background: "transparent", boxShadow: "none" }}>
                <label style={{ fontSize: "12px", color: "rgba(255,255,255,0.7)", paddingLeft: "5px", fontWeight: "bold" }}>
                  🌐 Language
                </label>
                <select
                  value={i18n.language}
                  onChange={(e) => changeLanguage(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    background: "rgba(255,255,255,0.2)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.4)",
                    borderRadius: "8px",
                    outline: "none",
                    cursor: "pointer",
                    fontWeight: "bold",
                    appearance: "none" 
                  }}
                >
                  <option value="en" style={{ color: "#333" }}>🇬🇧 English</option>
                  <option value="tpi" style={{ color: "#333" }}>🇵🇬 Tok Pisin</option>
                  <option value="fr" style={{ color: "#333" }}>🇫🇷 Français</option>
                  <option value="es" style={{ color: "#333" }}>🇪🇸 Español</option>
                  <option value="pt" style={{ color: "#333" }}>🇵🇹 Português</option>
                  <option value="zh" style={{ color: "#333" }}>🇨🇳 中文 (Chinese)</option>
                  <option value="ar" style={{ color: "#333" }}>🇸🇦 العربية (Arabic)</option>
                  <option value="ru" style={{ color: "#333" }}>🇷🇺 Русский (Russian)</option>
                  <option value="hi" style={{ color: "#333" }}>🇮🇳 हिन्दी (Hindi)</option>
                  <option value="id" style={{ color: "#333" }}>🇮🇩 Bahasa Indonesia</option>
                </select>
              </li>
            </ul>
      
<div
  style={{
    marginTop: "auto",
    textAlign: "center",
    fontSize: "11px",
    color: "#dbeafe",
    padding: "10px",
    borderTop: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(255,255,255,0.05)",
    borderRadius: "10px",
    backdropFilter: "blur(6px)",
  }}
>
  © 2026 KinaTrip Technologies Ltd.
  <br />
  All Rights Reserved.
</div>


          </>
        )}
      </div>

      {liveChatPopup && (
        <div className="chat-popup">
          <div className="chat-box">
            <h3>Live Chat (Admin)</h3>
            <div className="chat-messages">
              {liveChatMessages.map(msg => (
                <div key={msg.id} className={`chat-message ${msg.sender}`}>
                  {msg.type === "text" && <p>{msg.content}</p>}
                  {msg.type === "file" && <a href={msg.fileUrl} target="_blank" rel="noreferrer">📎 Download File</a>}
                  {msg.type === "productKey" && <p>🔑 Product Key: {msg.content}</p>}
                </div>
              ))}
            </div>
            <div className="chat-input">
              <input type="text" value={newLiveMsg} onChange={e => setNewLiveMsg(e.target.value)} placeholder="Type message..." />
              <input type="file" onChange={e => setFileUpload(e.target.files[0])} />
              <button onClick={sendLiveMessage}>Send</button>
            </div>
            <button className="close-btn" onClick={() => setLiveChatPopup(false)}>{t('close') || "Close"}</button>
          </div>
        </div>
      )}

      {productKeyPopup && (
        <div className="productkey-popup-overlay">
          <div className="productkey-popup">
            <h3>{t('enter_key') || "Enter Product Key"}</h3>
            <input type="text" placeholder="Enter your product key..." value={enteredKey} onChange={(e) => setEnteredKey(e.target.value)} />
            <button onClick={submitProductKey}>Submit Key</button>
            <button className="close-btn" onClick={() => setProductKeyPopup(false)}>{t('close') || "Close"}</button>
          </div>
        </div>
      )}

      {historyPopup && (
        <div className="chat-popup">
          <div className="chat-box">
            <h3>Ride History</h3>
            <div className="chat-messages">
              {rideHistory.length === 0 && <p>No completed rides yet.</p>}
              {rideHistory.map((ride) => {
                const passenger = ride.passengerProfile || {};
                return (





                  <div key={ride.id} className="history-card">
                    <img src={passenger.photoUrl || passenger.photoURL || "/default-avatar.png"} alt={passenger.name} style={{ width: 50, height: 50, borderRadius: "50%", objectFit: "cover", marginBottom: "5px" }} />
                    <p><b>{passenger.name || "Passenger"}</b></p>
                    <p>{t('fare') || "Fare"}: {formatCurrency(ride.fare, userCountry)}</p>
                    <p>Status: <span style={{color:"green"}}>Completed</span></p>
                    <p>Date: {new Date(ride.timestamp || Date.now()).toLocaleDateString()}</p>
                  </div>
                );
              })}
            </div>
            <button className="close-btn" onClick={() => setHistoryPopup(false)}>{t('close') || "Close"}</button>
          </div>
        </div>
      )}





{licensePopup && (
  <div className="license-popup">
    <div className="license-box">
      <h3>{t("select_license_plan")}</h3>

      {!selectedLicensePlan ? (
        <div className="license-plan-grid">
          {licensePlans.map((plan) => (
            <button
              key={plan.id}
              className={`license-plan-card ${plan.premium ? "premium" : ""}`}
              onClick={() => {
                setSelectedLicensePlan(plan);
                setPaymentAmount(plan.price);
              }}
            >
              {plan.premium && (
                <div className="premium-badge">{t("premium")}</div>
              )}

              <div className="license-plan-title">{plan.name}</div>

              <div className="license-plan-price">
                {plan.price === 0
                  ? t("free")
                  : formatLicensePrice(
                      plan.price,
                      exchangeRates[getCurrencyCode(userCountry)],
                      userCountry
                    )}
              </div>

              <div className="license-plan-info">
                {t("validity")}: {plan.days} {t("days_label")}
              </div>

              <div className="license-plan-info">
                {t("daily_limit")}:{" "}
                {plan.dailyLimit === "Unlimited"
                  ? t("unlimited_passengers")
                  : `${plan.dailyLimit} ${t("passengers")}`}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <>
          <h3>{selectedLicensePlan.name}</h3>

          <p>
            {t("fee")}:{" "}
            <b>
              {selectedLicensePlan.price === 0
                ? t("free")
                : formatLicensePrice(
                    selectedLicensePlan.price,
                    exchangeRates[getCurrencyCode(userCountry)],
                    userCountry
                  )}
            </b>
          </p>

          <p>
            {t("validity")}:{" "}
            <b>
              {selectedLicensePlan.days} {t("days_label")}
            </b>
          </p>

          <p>
            {t("daily_limit")}:{" "}
            <b>
              {selectedLicensePlan.dailyLimit === "Unlimited"
                ? t("unlimited_passengers")
                : `${selectedLicensePlan.dailyLimit} ${t("passengers")}`}
            </b>
          </p>
        </>
      )}

      {selectedLicensePlan?.price === 0 && (
        <button
          disabled={
            licenseData?.licenseType === "trial" &&
            Date.now() < licenseData?.expiry
          }
          onClick={async () => {
            const now = Date.now();

            const trialLicense = {
              status: "approved",
              licenseType: "trial",
              activatedAt: now,
              expiry:
                now +
                selectedLicensePlan.days * 24 * 60 * 60 * 1000,
              dailyLimit: selectedLicensePlan.dailyLimit,
              method: "trial",
            };

            await set(ref(db, `licenses/${user.uid}`), trialLicense);

            setLicenseData(trialLicense);
            showAlert(t("trial_activated_success"), "success");

            setLicensePopup(false);
            setSelectedLicensePlan(null);
          }}
          style={{
            width: "100%",
            padding: "12px",
            background:
              licenseData?.licenseType === "trial" &&
              Date.now() < licenseData?.expiry
                ? "#555"
                : "#00c853",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            fontWeight: "bold",
            cursor:
              licenseData?.licenseType === "trial" &&
              Date.now() < licenseData?.expiry
                ? "not-allowed"
                : "pointer",
            marginTop: "12px",
          }}
        >
          {licenseData?.licenseType === "trial" &&
          Date.now() < licenseData?.expiry
            ? `${t("trial_active")} (${licenseCountdown})`
            : `🚀 ${t("activate_free_trial")}`}
        </button>
      )}

      {selectedLicensePlan?.price > 0 && !cashDepositMode && !cardMode && (
        <>
          <h4>{t("select_payment_type")}</h4>

          <button onClick={() => setCashDepositMode(true)}>
            💵 {t("cash_deposit")}
          </button>

          <button onClick={() => setCardMode(true)}>
            💳 {t("online_card_payment")}
          </button>
        </>
      )}

      {cashDepositMode && (
        <>
          <h4>{t("cash_deposit_payment")}</h4>

          <p className="deposit-divider">
            {t("pay_through_partner")}<br />
            {t("country")}: <b>{userCountry}</b><br />
            {t("plan")}: <b>{selectedLicensePlan?.name}</b><br />
            {t("amount")}:{" "}
            <b>
              {formatLicensePrice(
                selectedLicensePlan?.price || 0,
                exchangeRates[getCurrencyCode(userCountry)],
                userCountry
              )}
            </b>
            <br />
          </p>

          <input
            type="text"
            placeholder={t("full_name")}
            value={paymentFullName}
            onChange={(e) => setPaymentFullName(e.target.value)}
          />

          <input
            type="tel"
            placeholder={t("phone_number")}
            value={paymentPhone}
            onChange={(e) => setPaymentPhone(e.target.value)}
          />

          <input
            type="number"
            placeholder={t("amount_paid")}
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
          />

          <input
            type="text"
            placeholder={t("receipt_reference")}
            value={paymentReference}
            onChange={(e) => setPaymentReference(e.target.value)}
          />

          <label>{t("upload_receipt")}</label>
<label className="custom-upload-btn">
  {t("upload_receipt")}
  
  <input
    type="file"
    accept="image/*"
    onChange={handleUploadScreenshot}
    hidden
  />
</label>

<p style={{ fontSize: "12px", color: "#bbb" }}>
  {screenshotUrl
    ? t("receipt_uploaded")
    : t("no_file_selected")}
</p>          

          {screenshotUrl && (
            <img
              src={screenshotUrl}
              alt={t("payment_screenshot_alt")}
              style={{ width: "200px", marginTop: "10px", borderRadius: "8px" }}
            />
          )}

          <button onClick={submitCashPayment}>
            {t("submit_cash_payment")}
          </button>

          <button onClick={() => setCashDepositMode(false)}>
            {t("back_to_payment_type")}
          </button>
        </>
      )}

      {cardMode && (
        <>
          <h4>{t("online_card_payment")}</h4>

          <p>
            {t("pay")}{" "}
            <b>
              {formatLicensePrice(
                selectedLicensePlan?.price || 0,
                exchangeRates[getCurrencyCode(userCountry)],
                userCountry
              )}
            </b>{" "}
            {t("securely_using")}:
          </p>

          <div style={{ display: "grid", gap: "8px", margin: "10px 0" }}>
            <button onClick={() => handleOnlinePayment("stripe")}>
              🌍 {t("stripe_payment")}
            </button>

            <button onClick={() => handleOnlinePayment("flutterwave")}>
              🌍 {t("flutterwave_payment")}
            </button>

            <button onClick={() => handleOnlinePayment("xendit")}>
              🌍 {t("xendit_payment")}
            </button>
          </div>

<button
  className="paypal-btn"
  onClick={() => handleOnlinePayment("paypal")}
>
  🅿️ {t("paypal_payment")}
</button>




          <small
            style={{
              display: "block",
              marginTop: "10px",
              color: "#bbb",
              fontSize: "12px",
            }}
          >
            {t("secure_payment_text")}
          </small>

          <button onClick={() => setCardMode(false)}>
            {t("back_to_payment_type")}
          </button>
        </>
      )}

      <button
        onClick={() => {
          setLicensePopup(false);
          setCashDepositMode(false);
          setCardMode(false);
          setSelectedLicensePlan(null);
        }}
      >
        {t("close")}
      </button>
    </div>
  </div>
)}
```



      {showMarket && (
        <Market onClose={() => setShowMarket(false)} />
      )}

      {showSocialProfile && (
        <DriverProfileOverlay 
          driver={{ ...driverProfile, id: user.uid }} 
          onClose={() => setShowSocialProfile(false)} 
        />
      )}

  {selectedPassengerForProfile && (
  <PassengerSocialProfile
    passengerProfile={selectedPassengerForProfile}
    onClose={() => setSelectedPassengerForProfile(null)}
  />
)}

    </div>
  </>
  );
};

export default DriverHome;


